import { useCallback, useState, type ReactNode } from 'react';

import { ConfirmDialog } from './ConfirmDialog';
import { Switch } from './Switch';

export type ConfirmSwitchConfirm = {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  tone?: 'default' | 'danger';
};

export type ConfirmSwitchProps = {
  checked: boolean;
  onCheckedChange?: (next: boolean) => void;
  guardWhen: 'off' | ((next: boolean) => boolean);
  confirm: ConfirmSwitchConfirm;
  /** Typed-to-confirm gate (case-insensitive), like ConfirmDelete. */
  confirmation?: { expected: string };
  onConfirm: () => void | Promise<void>;
  saving?: boolean;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
};

function isGuarded(
  next: boolean,
  guardWhen: ConfirmSwitchProps['guardWhen'],
): boolean {
  if (guardWhen === 'off') return !next;
  return guardWhen(next);
}

/**
 * Switch that opens `ConfirmDialog` on a guarded transition. Unguarded
 * transitions call `onCheckedChange` immediately; guarded ones commit
 * `onConfirm` only after confirm. Cancel/Escape leaves `checked` unchanged.
 */
export function ConfirmSwitch({
  checked,
  onCheckedChange,
  guardWhen,
  confirm,
  confirmation,
  onConfirm,
  saving = false,
  disabled = false,
  id,
  'aria-label': ariaLabel,
}: ConfirmSwitchProps) {
  const [open, setOpen] = useState(false);

  const handleSwitchChange = useCallback(
    (next: boolean) => {
      if (disabled || saving) return;
      if (isGuarded(next, guardWhen)) {
        setOpen(true);
      } else {
        onCheckedChange?.(next);
      }
    },
    [disabled, saving, guardWhen, onCheckedChange],
  );

  const handleConfirm = useCallback(async () => {
    await onConfirm();
  }, [onConfirm]);

  return (
    <>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={handleSwitchChange}
        disabled={disabled || saving}
        aria-label={ariaLabel}
      />
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={confirm.title}
        description={confirm.description}
        confirmLabel={confirm.confirmLabel}
        tone={confirm.tone ?? 'danger'}
        confirmation={
          confirmation
            ? {
                expected: confirmation.expected,
                label: `Type "${confirmation.expected}" to confirm`,
                placeholder: confirmation.expected,
              }
            : undefined
        }
        onConfirm={handleConfirm}
      />
    </>
  );
}
